// @aiContributed-MAPFuse-2026-03-05
import React, { Suspense, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { LayoutContainer, Layout, layoutConfigs } from '@bingads-webui-react/app-layout-container';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { PerformanceMarkerProvider } from '@bingads-webui-campaign-react/performance-marker-provider';
import { withPageReadyAsync } from '@bingads-webui-react/with-page-ready';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';
import { PmaxLiteSignupAppCoreExt } from './pmax-lite-signup-app-core-ext';

const DelayedAfterPageReady = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */ './after-page-ready').then(({ AfterPageReady }) => ({
    default: AfterPageReady,
  })));

const DelayedAdBlockerBanner = withPageReadyAsync(() =>
  import(/*  webpackMode: 'eager' */'@bingads-webui-campaign-react/ad-blocker-banner').then(({ AdBlockerBanner }) => ({
    default: AdBlockerBanner,
  })));

const DelayedPromotionBanner = withPageReadyAsync(() =>
  import(/*  webpackMode: 'eager' */'@bingads-webui-campaign-react/promotion-banner').then(({ PromotionBanner }) => ({
    default: PromotionBanner,
  })));

const DelayedSDKHeader = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/sdk-header').then(({ SDKHeader }) => ({
    default: SDKHeader,
  })));

const DelayedGlobalMenu = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */ './global-menu').then(({ GlobalMenu }) => ({
    default: GlobalMenu,
  })));

const DelayedCopilotLiveAgentWithI18n = withPageReadyAsync(() =>
  import(/* webpackMode: 'eager' */ './support-components/copilot-live-agent').then(({ CopilotLiveAgentWithI18n }) => ({
    default: CopilotLiveAgentWithI18n,
  })));

const HeaderComponent = isSDK() ? DelayedSDKHeader : DelayedGlobalMenu;

const MainContentWrapper = ({ isCopilotLiveAgentEnabled = false, children }) => {
  if (isCopilotLiveAgentEnabled && !isSDK()) {
    return <main id="main">{children}</main>;
  }
  return children;
};

export const PmaxLiteSignupAppCore = ({
  devConfig,
}) => {
  const instrumentation = useContext(InstrumentationContext);

  const isCopilotLiveAgentEnabled = usePilotsValue('IsPMaxLiteCopilotLiveAgentEnabled');
  const PmaxLiteSignupContent = useCallback(() => <PmaxLiteSignupAppCoreExt devConfig={devConfig} />, [devConfig]);

  return (
    <PerformanceMarkerProvider
      name="ShellPage_PmaxLiteSignupUI"
      instrumentation={instrumentation}
      immediateDone={false}
    >
      <LayoutContainer
        pageConfig={layoutConfigs.signupScreen}
        persistConfig={layoutConfigs.signupScreen}
        isGridLayout={isCopilotLiveAgentEnabled && !isSDK()}
      >
        <Suspense fallback={null}>
          <Layout key="header" item="header" component={HeaderComponent} />
          <Layout key="topBanner" item="topBanner" component={DelayedAdBlockerBanner} />
          <Layout key="copilotLiveAgent" item="copilot" component={DelayedCopilotLiveAgentWithI18n} />
        </Suspense>
        <MainContentWrapper isCopilotLiveAgentEnabled={isCopilotLiveAgentEnabled}>
          <Layout key="promotionBanner" item="promotionBanner" component={DelayedPromotionBanner} />
          <Layout key="content" item="content" component={PmaxLiteSignupContent} />
        </MainContentWrapper>
        <DelayedAfterPageReady />
      </LayoutContainer>
    </PerformanceMarkerProvider>
  );
};

PmaxLiteSignupAppCore.propTypes = {
  devConfig: PropTypes.shape({
    isInDevelopmentMode: PropTypes.bool.isRequired,
  }).isRequired,
};

PmaxLiteSignupAppCore.defaultProps = {
};
